import { Button, Checkbox, Input, notification, Tooltip } from "antd";
import { useFilteredDetections } from "components/hooks/contexts/FilteredDetections";
import { getPositionForInspectionImage, parseRoutePositionData } from "components/util/Geometry";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    customAudit,
    exportInspectionDetectionsV2,
    resetInspectionDetectionsExportIndexRange,
    setInspectionDetectionsExportIndexRangeValue,
    toggleInspectionDetectionsExport,
} from "redux/actions";
import { routeGeomSelector } from "redux/selectors";

const userEmailSelector = (state) => state.userDetails.email;
const railInspectionImagesSelector = (state) => state.railInspection.railInspectionImages.data;
const selectedRailInspectionImageSelector = (state) => state.railInspection.selectedRailInspectionImage;
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const detectionsTypeFiltersSelector = (state) => state.railInspection.detections.typeFilters;
const detectionsReviewFiltersSelector = (state) => state.railInspection.detections.reviewFilters;
const sessionIDSelector = (state) => state.playlist.data.routeID;
const detectionsExportIndexRangeSelector = (state) => state.railInspection.detectionsExportIndexRange;

const DetectionsExport = () => {
    const dispatch = useDispatch();

    const userEmail = useSelector(userEmailSelector);
    const railInspectionImages = useSelector(railInspectionImagesSelector);
    const selectedRailInspectionImage = useSelector(selectedRailInspectionImageSelector);
    const routeGeometryData = useSelector(routeGeomSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const detectionsTypeFilters = useSelector(detectionsTypeFiltersSelector);
    const detectionsReviewFilters = useSelector(detectionsReviewFiltersSelector);
    const sessionID = useSelector(sessionIDSelector);
    const detectionsExportIndexRange = useSelector(detectionsExportIndexRangeSelector);

    const [exportName, setExportName] = useState("");
    const [recipientEmail, setRecipientEmail] = useState(userEmail);
    const [includeImages, setIncludeImages] = useState(true);

    const filteredDetections = useFilteredDetections();

    const filteredDetectionsCount = useMemo(() => {
        let startTimestamp = null;
        if (detectionsExportIndexRange.start) {
            startTimestamp = railInspectionImages[detectionsExportIndexRange.start].timestamp;
        }
        let endTimestamp = null;
        if (detectionsExportIndexRange.end) {
            endTimestamp = railInspectionImages[detectionsExportIndexRange.end].timestamp;
        }

        return filteredDetections.filter((item) => {
            if (startTimestamp !== null && item.image_timestamp < startTimestamp) {
                return false;
            }

            if (endTimestamp !== null && item.image_timestamp > endTimestamp) {
                return false;
            }

            return true;
        }).length;
    }, [filteredDetections, detectionsExportIndexRange.start, detectionsExportIndexRange.end, railInspectionImages]);

    useEffect(() => {
        if (filteredDetectionsCount > 750) {
            setIncludeImages(false);
        }
    }, [filteredDetectionsCount]);

    const formatMileage = useCallback(
        (index) => {
            if (index > -1) {
                const position = getPositionForInspectionImage(railInspectionImages[index], routeGeometryData);
                const result = parseRoutePositionData(position, routeSystemID);
                if (result) {
                    return result.string;
                }
            }
        },
        [railInspectionImages, routeGeometryData, routeSystemID],
    );

    const startLocation = useMemo(() => {
        if (detectionsExportIndexRange.start === null) {
            return formatMileage(0);
        } else {
            return formatMileage(detectionsExportIndexRange.start);
        }
    }, [detectionsExportIndexRange.start, formatMileage]);

    const endLocation = useMemo(() => {
        if (detectionsExportIndexRange.end === null) {
            return formatMileage(railInspectionImages.length - 1);
        } else {
            return formatMileage(detectionsExportIndexRange.end);
        }
    }, [detectionsExportIndexRange.end, formatMileage, railInspectionImages.length]);

    const createExport = () => {
        const parameters = {
            name: exportName,
            session_id: sessionID,
            review_filters: detectionsReviewFilters,
            detection_types: detectionsTypeFilters,
        };

        if (!(detectionsExportIndexRange.start === null && detectionsExportIndexRange.end === null)) {
            const startIndex = detectionsExportIndexRange.start || 0;
            const endIndex = detectionsExportIndexRange.end || railInspectionImages.length - 1;

            parameters["lower_bound_ms"] = railInspectionImages[startIndex].timestamp;
            parameters["upper_bound_ms"] = railInspectionImages[endIndex].timestamp;
        }

        const exportPayload = {
            export_type: "inspection_detections",
            filters: {},
            email_to: recipientEmail,
            include_images: includeImages,
            parameters,
        };

        dispatch(customAudit("inspection_detections_navigator.create_export", exportPayload, `Inspection detections navigator - create export: ${exportName}`));

        dispatch(exportInspectionDetectionsV2(exportPayload))
            .then(() => {
                notification.success({
                    message: "Successfully triggered export",
                });
                dispatch(toggleInspectionDetectionsExport());
                dispatch(resetInspectionDetectionsExportIndexRange());
            })
            .catch(() => {
                notification.error({
                    message: "Error triggering export",
                });
                dispatch(toggleInspectionDetectionsExport());
                dispatch(resetInspectionDetectionsExportIndexRange());
            });
    };

    return (
        <div className="InspectionDetectionsExport">
            <div className="InspectionDetectionsExport_Body">
                <div>
                    <span>Export Name</span>
                    <Input
                        value={exportName}
                        onChange={(e) => setExportName(e.target.value)}
                    />
                </div>
                <div>
                    <span>Recipient Email</span>
                    <Input
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                </div>
                {filteredDetectionsCount > 750 ? (
                    <Tooltip title="Images cannot be included when exporting more than 750 detections">
                        <span style={{ width: "fit-content" }}>
                            <Checkbox
                                disabled={true}
                                checked={includeImages}>
                                Include images
                            </Checkbox>
                        </span>
                    </Tooltip>
                ) : (
                    <Checkbox
                        checked={includeImages}
                        onChange={(e) => setIncludeImages(e.target.checked)}>
                        Include images
                    </Checkbox>
                )}
                <div className="RangeButtons">
                    <div className="RangeButton">
                        <span>Start Position</span>
                        <div>
                            <span>{startLocation}</span>
                            <Tooltip title="Sets the start position to the currently selected location">
                                <Button
                                    type="default"
                                    size="small"
                                    onClick={() => dispatch(setInspectionDetectionsExportIndexRangeValue("start", selectedRailInspectionImage.index))}>
                                    Set to current position
                                </Button>
                            </Tooltip>
                            {detectionsExportIndexRange.start !== null && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => dispatch(setInspectionDetectionsExportIndexRangeValue("start", null))}>
                                    reset
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="RangeButton">
                        <span>End Position</span>
                        <div>
                            <span>{endLocation}</span>
                            <Tooltip title="Selects the current location as the end position">
                                <Button
                                    type="default"
                                    size="small"
                                    onClick={() => dispatch(setInspectionDetectionsExportIndexRangeValue("end", selectedRailInspectionImage.index))}>
                                    Set to current position
                                </Button>
                            </Tooltip>
                            {detectionsExportIndexRange.end !== null && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => dispatch(setInspectionDetectionsExportIndexRangeValue("end", null))}>
                                    reset
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="ActionButtons">
                <Button
                    onClick={() => {
                        dispatch(toggleInspectionDetectionsExport());
                        dispatch(resetInspectionDetectionsExportIndexRange());
                    }}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={createExport}
                    disabled={exportName === "" || filteredDetectionsCount === 0}>
                    <span>
                        Export <strong>{filteredDetectionsCount}</strong> detections
                    </span>
                </Button>
            </div>
        </div>
    );
};

export default DetectionsExport;
